import * as React from "react";
import CSVUpload from "../components/pricing/CSVUpload";
import InnerPage from "../components/InnerPage";
import { useContext } from "react";
import { AppContext } from "../App";
import { Box, Typography, Paper, CircularProgress, Alert } from "@mui/material";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    console.error("Error in ViewPricelistsScreen:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          color: 'white', 
          backgroundColor: 'black',
          border: '1px solid red' 
        }}>
          <h2>Something went wrong.</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function ViewPricelistsScreen({ user }) {
  console.log("Rendering ViewPricelistsScreen with user:", user);
  const { userRecord } = useContext(AppContext);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  // Use either passed user prop or context userRecord
  const effectiveUser = user || userRecord;
  
  React.useEffect(() => {
    console.log("ViewPricelistsScreen mounted");
    console.log("User from props:", user);
    console.log("User from context:", userRecord);
    
    // Only consider loaded when we have a user object from either source
    if (effectiveUser) {
      setLoading(false);
    } else {
      // Set a timeout to prevent infinite loading if context never resolves
      const timer = setTimeout(() => {
        if (!effectiveUser) {
          setLoading(false);
          setError("Failed to load user data after timeout");
        }
      }, 5000);
      
      return () => clearTimeout(timer);
    }
    
    return () => {
      console.log("ViewPricelistsScreen unmounted");
    };
  }, [user, userRecord, effectiveUser]);

  if (loading) {
    return (
      <InnerPage title="View Pricelists">
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            p: 4,
            color: 'white'
          }}
        >
          <CircularProgress color="primary" />
          <Typography sx={{ mt: 2 }}>Loading user data...</Typography>
        </Box>
      </InnerPage>
    );
  }

  if (error) {
    return (
      <InnerPage title="View Pricelists">
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
        <Paper sx={{ p: 3, m: 2, backgroundColor: '#333', color: 'white' }}>
          <Typography variant="h6">Debug Information:</Typography>
          <Typography>User prop: {JSON.stringify(user)}</Typography>
          <Typography>Context user: {JSON.stringify(userRecord)}</Typography>
        </Paper>
      </InnerPage>
    );
  }

  if (!effectiveUser) {
    return (
      <InnerPage title="View Pricelists">
        <Alert severity="warning" sx={{ m: 2 }}>
          No user data available. This might be a user context issue.
        </Alert>
        <Paper sx={{ p: 3, m: 2, backgroundColor: '#333', color: 'white' }}>
          <Typography variant="h6">Debug Information:</Typography>
          <Typography>User prop: {JSON.stringify(user)}</Typography>
          <Typography>Context user: {JSON.stringify(userRecord)}</Typography>
        </Paper>
      </InnerPage>
    );
  }

  return (
    <ErrorBoundary>
      <InnerPage title="View Pricelists">
        <div style={{ padding: '20px', color: 'white' }}>
          <h2>View Pricelists Content</h2>
          <Box sx={{ mb: 2, p: 2, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 1 }}>
            <Typography variant="subtitle2">Debug: User ID: {effectiveUser?.id}</Typography>
          </Box>
          <CSVUpload user={effectiveUser} />
        </div>
      </InnerPage>
    </ErrorBoundary>
  );
}
